"use client";

import { useState } from "react";
import {
  Menu,
  X,
  Check,
  Users,
  Leaf,
  Star,
  Beaker,
  XCircle,
} from "lucide-react";
import Modal from "../components/ui/Modal";
import Hero from "../components/Hero";
import emailjs from "emailjs-com";
import { ClipLoader } from "react-spinners";

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isloading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("");

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Replace these with your EmailJS credentials
    const serviceID = "service_cwyirwq";
    const templateID = "template_tvsggei";
    const userID = "sg9yvEl35cCIlsNKn";

    setIsLoading(true);

    emailjs.send(serviceID, templateID, formData, userID).then(
      (response) => {
        setIsLoading(false);
        handleMessage("Message sent successfully!");
        setStatus("good");

        setFormData({
          name: "",
          email: "",
          subject: "",
          message: "",
        });
      },
      (error) => {
        setIsLoading(false);
        handleMessage("Failed to send message. Please try again.");
        setStatus("error");
      }
    );
  };

  const handleMessage = (message)=> {
    setMessage(message);
    setTimeout(()=> {
      setMessage("")
    }, 3000)

  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center">
            <img src="./dagiLogo.png" alt="dagi logo" className="h-8 lg:h-10" />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            <a
              href="#about"
              className="text-gray-700 hover:text-orange-500 font-medium"
            >
              About Us
            </a>
            <a
              href="#products"
              className="text-gray-700 hover:text-orange-500 font-medium"
            >
              Products
            </a>
            <a
              href="#services"
              className="text-gray-700 hover:text-orange-500 font-medium"
            >
              Services
            </a>
            <a
              href="#values"
              className="text-gray-700 hover:text-orange-500 font-medium"
            >
              Values
            </a>
            <a
              href="#team"
              className="text-gray-700 hover:text-orange-500 font-medium"
            >
              Team
            </a>
            <a
              href="#contact"
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition"
            >
              Contact Us
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button className="md:hidden" onClick={toggleMenu}>
            {isMenuOpen ? (
              <X className="h-6 w-6 text-gray-700" />
            ) : (
              <Menu className="h-6 w-6 text-gray-700" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden bg-white py-4 px-4 shadow-lg">
            <nav className="flex flex-col space-y-4">
              <a
                href="#about"
                className="text-gray-700 hover:text-orange-500 font-medium"
                onClick={toggleMenu}
              >
                About Us
              </a>
              <a
                href="#products"
                className="text-gray-700 hover:text-orange-500 font-medium"
                onClick={toggleMenu}
              >
                Products
              </a>
              <a
                href="#services"
                className="text-gray-700 hover:text-orange-500 font-medium"
                onClick={toggleMenu}
              >
                Services
              </a>
              <a
                href="#values"
                className="text-gray-700 hover:text-orange-500 font-medium"
                onClick={toggleMenu}
              >
                Values
              </a>
              <a
                href="#team"
                className="text-gray-700 hover:text-orange-500 font-medium"
                onClick={toggleMenu}
              >
                Team
              </a>
              <a
                href="#contact"
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition text-center"
                onClick={toggleMenu}
              >
                Contact Us
              </a>
            </nav>
          </div>
        )}
      </header>

      <main>
        {/* Hero Section */}
        <Hero />

        {/* About Us Section */}
        <section id="about" className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-green-700 mb-4">
                About <span className="text-orange-500">Us</span>
              </h2>
              <div className="w-20 h-1 bg-orange-500 mx-auto"></div>
            </div>

            <div className="flex flex-col md:flex-row gap-10">
              <div className="md:w-1/2">
                <h3 className="text-2xl font-semibold text-green-700 mb-4">
                  Company Overview
                </h3>
                <p className="text-gray-700 mb-6">
                  De Ashymai Global Investment Limited (DAGI) is a leading
                  manufacturer of professional poultry feed products (DAGI FEED)
                  and provider of animal nutrition services. Our mission is to
                  deliver high-quality, nutritious feed solutions that enhance
                  the health and productivity of poultry and livestock, while
                  fostering sustainable agricultural practices and supporting
                  the growth of our customers.
                </p>

                <h3 className="text-2xl font-semibold text-green-700 mb-4">
                  Our Vision
                </h3>
                <p className="text-gray-700">
                  To become the trusted partner of choice for poultry farmers,
                  animal nutritionists, and agricultural businesses worldwide,
                  by providing innovative, research-driven feed products and
                  expert services that promote animal well-being, environmental
                  sustainability, and economic prosperity.
                </p>
              </div>
              <div className="md:w-1/2">
                <img
                  src="./operation.jpeg"
                  alt="Our Facility"
                  className="rounded-lg shadow-xl mb-6"
                />
                <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-600">
                  <h4 className="text-xl font-semibold text-green-700 mb-2">
                    Why Choose DAGI?
                  </h4>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-orange-500 mr-2 mt-1" />
                      <span>
                        Premium quality feed formulations backed by research
                      </span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-orange-500 mr-2 mt-1" />
                      <span>Expert nutrition consulting services</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-orange-500 mr-2 mt-1" />
                      <span>
                        Commitment to sustainable agricultural practices
                      </span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-orange-500 mr-2 mt-1" />
                      <span>Dedicated customer support and service</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Products Section */}
        <section id="products" className="py-20 bg-green-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-green-700 mb-4">
                Our <span className="text-orange-500">Products</span>
              </h2>
              <div className="w-20 h-1 bg-orange-500 mx-auto mb-6"></div>
              <p className="text-gray-700 max-w-3xl mx-auto">
                We offer a comprehensive range of high-quality feed products
                designed to meet the specific nutritional needs of poultry and
                livestock at every stage of development.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition">
                {/* <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mb-4"> */}
                <img
                  src="./starter.jpg"
                  alt="Broiler Feed"
                  className="h-10 w-10 md:w-16 md:h-16"
                />
                {/* </div> */}
                <h3 className="text-xl font-semibold text-green-700 mb-2">
                  Broiler Feed
                </h3>
                <p className="text-gray-700">
                  High-performance feed formulations specifically designed for
                  broiler chickens at different growth stages.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition">
                {/* <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mb-4"> */}
                <img
                  src="./layer.jpg"
                  alt="Layer Feed"
                  className="h-10 w-10 md:w-16 md:h-16"
                />
                {/* </div> */}
                <h3 className="text-xl font-semibold text-green-700 mb-2">
                  Layer Feed
                </h3>
                <p className="text-gray-700">
                  Nutritionally balanced feed formulations for laying hens to
                  optimize egg production and quality.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition">
                {/* <div className="bg-orange-100 rounded-full w-16 h-16 md:w-20 md:h-20 flex items-center justify-center mb-4"> */}
                <img
                  src="./finisher.jpg"
                  alt="Breeder Feed"
                  className="h-10 w-10 md:w-16 md:h-16"
                />
                {/* </div> */}
                <h3 className="text-xl font-semibold text-green-700 mb-2">
                  Breeder Feed
                </h3>
                <p className="text-gray-700">
                  Specialized feed formulations for breeding poultry to enhance
                  reproductive performance and chick quality.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition">
                {/* <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mb-4"> */}
                <img
                  src="./grower.jpg"
                  alt="Custom Feed"
                  className="h-10 w-10 md:w-16 md:h-16"
                />
                {/* </div> */}
                <h3 className="text-xl font-semibold text-green-700 mb-2">
                  Custom Feed Solutions
                </h3>
                <p className="text-gray-700">
                  Tailored feed formulations designed to meet specific
                  production goals and address unique nutritional challenges.
                </p>
              </div>
            </div>

            <div className="text-center mt-12">
              <a
                href="#contact"
                className="bg-orange-500 text-white px-6 py-3 rounded-md hover:bg-orange-600 transition inline-block"
              >
                Request Product Information
              </a>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-green-700 mb-4">
                Our <span className="text-orange-500">Services</span>
              </h2>
              <div className="w-20 h-1 bg-orange-500 mx-auto mb-6"></div>
              <p className="text-gray-700 max-w-3xl mx-auto">
                Beyond our premium feed products, we offer a range of expert
                services to help our customers optimize their animal nutrition
                programs and achieve their production goals.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition border-t-4 border-green-600">
                <h3 className="text-xl font-semibold text-green-700 mb-4">
                  Feed Formulation and Manufacturing
                </h3>
                <p className="text-gray-700 mb-4">
                  We develop and produce high-quality feed formulations tailored
                  to meet the specific nutritional requirements of different
                  poultry and livestock species and production stages.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-orange-500 mr-2 mt-1" />
                    <span>Custom feed formulation</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-orange-500 mr-2 mt-1" />
                    <span>Quality ingredient sourcing</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-orange-500 mr-2 mt-1" />
                    <span>Precision manufacturing</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition border-t-4 border-green-600">
                <h3 className="text-xl font-semibold text-green-700 mb-4">
                  Animal Nutrition Consulting
                </h3>
                <p className="text-gray-700 mb-4">
                  Our team of experienced animal nutritionists provides expert
                  advice and guidance to help customers optimize their feeding
                  programs and address nutritional challenges.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-orange-500 mr-2 mt-1" />
                    <span>Nutritional assessment</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-orange-500 mr-2 mt-1" />
                    <span>Feed program development</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-orange-500 mr-2 mt-1" />
                    <span>Performance monitoring</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition border-t-4 border-green-600">
                <h3 className="text-xl font-semibold text-green-700 mb-4">
                  Feed Quality Control and Testing
                </h3>
                <p className="text-gray-700 mb-4">
                  We maintain rigorous quality control standards and conduct
                  comprehensive testing to ensure the safety, consistency, and
                  nutritional value of our feed products.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-orange-500 mr-2 mt-1" />
                    <span>Nutrient analysis</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-orange-500 mr-2 mt-1" />
                    <span>Contaminant screening</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-orange-500 mr-2 mt-1" />
                    <span>Quality certification</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition border-t-4 border-green-600">
                <h3 className="text-xl font-semibold text-green-700 mb-4">
                  Research and Development
                </h3>
                <p className="text-gray-700 mb-4">
                  We invest in ongoing research and development to create
                  innovative feed products and solutions that address emerging
                  challenges and opportunities in animal nutrition.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-orange-500 mr-2 mt-1" />
                    <span>Nutritional innovation</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-orange-500 mr-2 mt-1" />
                    <span>Performance trials</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-orange-500 mr-2 mt-1" />
                    <span>Industry collaboration</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section id="values" className="py-20 bg-green-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-green-700 mb-4">
                Our <span className="text-orange-500">Values</span>
              </h2>
              <div className="w-20 h-1 bg-orange-500 mx-auto mb-6"></div>
              <p className="text-gray-700 max-w-3xl mx-auto">
                Our core values guide everything we do, from product development
                to customer service, and reflect our commitment to excellence
                and sustainability.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition text-center">
                <div className="bg-orange-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <Star className="h-10 w-10 text-orange-500" />
                </div>
                <h3 className="text-xl font-semibold text-green-700 mb-4">
                  Quality
                </h3>
                <p className="text-gray-700">
                  We are committed to delivering high-quality products and
                  services that meet the needs of our customers.
                </p>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition text-center">
                <div className="bg-orange-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <Beaker className="h-10 w-10 text-orange-500" />
                </div>
                <h3 className="text-xl font-semibold text-green-700 mb-4">
                  Innovation
                </h3>
                <p className="text-gray-700">
                  We continuously invest in research and development to stay
                  ahead of the curve and address emerging trends and challenges
                  in the industry.
                </p>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition text-center">
                <div className="bg-orange-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <Leaf className="h-10 w-10 text-orange-500" />
                </div>
                <h3 className="text-xl font-semibold text-green-700 mb-4">
                  Sustainability
                </h3>
                <p className="text-gray-700">
                  We prioritize environmentally friendly practices and
                  sustainable agriculture methods that promote animal welfare
                  and minimize our ecological footprint.
                </p>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-md hover:shadow-xl transition text-center">
                <div className="bg-orange-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <Users className="h-10 w-10 text-orange-500" />
                </div>
                <h3 className="text-xl font-semibold text-green-700 mb-4">
                  Customer Focus
                </h3>
                <p className="text-gray-700">
                  We are dedicated to providing exceptional customer service,
                  support, and expertise to help our customers achieve their
                  goals.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Team Section */}
        {/* <section id="team" className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-green-700 mb-4">
                Our <span className="text-orange-500">Team</span>
              </h2>
              <div className="w-20 h-1 bg-orange-500 mx-auto mb-6"></div>
              <p className="text-gray-700 max-w-3xl mx-auto">
                Our team of experienced professionals includes animal
                nutritionists, feed technologists, and industry experts who are
                passionate about delivering exceptional products and services to
                our customers.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition text-center">
                <img
                  src="https://via.placeholder.com/200x200"
                  alt="Team Member"
                  className="w-32 h-32 rounded-full mx-auto mb-4 object-cover"
                />
                <h3 className="text-xl font-semibold text-green-700">
                  Ibrahim Caleb
                </h3>
                <p className="text-orange-500 mb-4">Chief Nutritionist</p>
                <p className="text-gray-700">
                  With over 15 years of experience in animal nutrition, Dr.
                  Ibrahim leads our research and development efforts.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition text-center">
                <img
                  src="https://via.placeholder.com/200x200"
                  alt="Team Member"
                  className="w-32 h-32 rounded-full mx-auto mb-4 object-cover"
                />
                <h3 className="text-xl font-semibold text-green-700">
                  Tunde Koye
                </h3>
                <p className="text-orange-500 mb-4">Feed Production Manager</p>
                <p className="text-gray-700">
                  Tunde ensures that all our feed products meet the highest
                  quality standards through rigorous quality control processes.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition text-center">
                <img
                  src="https://via.placeholder.com/200x200"
                  alt="Team Member"
                  className="w-32 h-32 rounded-full mx-auto mb-4 object-cover"
                />
                <h3 className="text-xl font-semibold text-green-700">
                  Michael Adebayo
                </h3>
                <p className="text-orange-500 mb-4">
                  Technical Support Specialist
                </p>
                <p className="text-gray-700">
                  Michael provides expert technical support and guidance to our
                  customers to help them optimize their feeding programs.
                </p>
              </div>
            </div>

            <div className="text-center mt-12">
              <a
                href="#contact"
                className="bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-700 transition inline-block"
              >
                Meet Our Full Team
              </a>
            </div>
          </div>
        </section> */}

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-green-600 to-green-700 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Enhance Your Animal Nutrition Program?
            </h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Contact us today to learn how our premium feed products and expert
              services can help you improve animal health, productivity, and
              profitability.
            </p>
            <a
              href="#contact"
              className="bg-orange-500 text-white px-8 py-4 rounded-md hover:bg-orange-600 transition inline-block text-lg font-medium"
            >
              Get in Touch
            </a>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-green-700 mb-4">
                Contact <span className="text-orange-500">Us</span>
              </h2>
              <div className="w-20 h-1 bg-orange-500 mx-auto mb-6"></div>
              <p className="text-gray-700 max-w-3xl mx-auto">
                Have questions or ready to get started? Reach out to our team
                for more information about our products and services.
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-12">
              <div className="lg:w-1/2">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                        placeholder="Your Name"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                        placeholder="Your Email"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="subject"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Subject
                    </label>
                    <input
                      type="text"
                      id="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                      placeholder="Subject"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Message
                    </label>
                    <textarea
                      id="message"
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                      placeholder="Your Message"
                    ></textarea>
                  </div>
                  {message && (
                    <div
                      className={`${
                        status === "good" ? "bg-green-500" : "bg-red-500"
                      } px-4 py-2 bg-opacity-30 mx-auto rounded-lg`}
                    >
                      <p
                        className={`${
                          status === "good" ? "text-green-500" : "text-red-500"
                        } text-[13px] text-center`}
                      >
                        {message}
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    className="bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-700 transition"
                  >
                    {isloading ? (
                      <ClipLoader size={16} color="white" />
                    ) : (
                      "Send Message"
                    )}
                  </button>
                </form>
              </div>

              <div className="lg:w-1/2 bg-green-50 p-8 rounded-lg">
                <h3 className="text-2xl font-semibold text-green-700 mb-6">
                  Get in Touch
                </h3>

                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="bg-orange-100 rounded-full w-10 h-10 flex items-center justify-center mr-4 mt-1">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-orange-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-green-700">
                        Address
                      </h4>
                      <p className="text-gray-700">
                        Ibodi along Oloronmbo ilesa university road, Ilesa
                        -Ibadan express way, Ilesa Osun state.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="bg-orange-100 rounded-full w-10 h-10 flex items-center justify-center mr-4 mt-1">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-orange-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-green-700">
                        Phone
                      </h4>
                      <p className="text-gray-700">+234-706-791-2191</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="bg-orange-100 rounded-full w-10 h-10 flex items-center justify-center mr-4 mt-1">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-orange-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-green-700">
                        Email
                      </h4>
                      <p className="text-gray-700">ashymaiglobal@gmail.com</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="bg-orange-100 rounded-full w-10 h-10 flex items-center justify-center mr-4 mt-1">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 text-orange-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-green-700">
                        Business Hours
                      </h4>
                      <p className="text-gray-700">
                        Monday - Saturday: 9:00 AM - 5:00 PM
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-green-800 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4">
                <span className="text-orange-400">De Ashymai</span> Global
              </h3>
              <p className="mb-4">
                Leading manufacturer of professional poultry feed products and
                provider of animal nutrition services.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-white hover:text-orange-400">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </a>
                <a href="#" className="text-white hover:text-orange-400">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                </a>
                <a href="#" className="text-white hover:text-orange-400">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                    />
                  </svg>
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li>
                  <a href="#about" className="hover:text-orange-400">
                    About Us
                  </a>
                </li>
                <li>
                  <a href="#products" className="hover:text-orange-400">
                    Products
                  </a>
                </li>
                <li>
                  <a href="#services" className="hover:text-orange-400">
                    Services
                  </a>
                </li>
                <li>
                  <a href="#values" className="hover:text-orange-400">
                    Values
                  </a>
                </li>
                <li>
                  <a href="#team" className="hover:text-orange-400">
                    Team
                  </a>
                </li>
                <li>
                  <a href="#contact" className="hover:text-orange-400">
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Products</h4>
              <ul className="space-y-2">
                <li>
                  <a href="#" className="hover:text-orange-400">
                    Broiler Feed
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-orange-400">
                    Layer Feed
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-orange-400">
                    Breeder Feed
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-orange-400">
                    Custom Solutions
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-orange-400">
                    Feed Additives
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Newsletter</h4>
              <p className="mb-4">
                Subscribe to our newsletter for the latest updates and insights.
              </p>
              <form className="space-y-2">
                <input
                  type="email"
                  className="w-full px-4 py-2 bg-green-700 border border-green-600 rounded-md focus:ring-orange-400 focus:border-orange-400 text-white placeholder-green-300"
                  placeholder="Your Email"
                />
                <button
                  type="submit"
                  className="w-full bg-orange-500 text-white px-4 py-2 rounded-md hover:bg-orange-600 transition"
                >
                  Subscribe
                </button>
              </form>
            </div>
          </div>

          <div className="border-t border-green-700 mt-8 pt-8 text-center">
            <p>
              &copy; {new Date().getFullYear()} De Ashymai Global Investment
              Limited. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
